import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import * as ep from '../api/endpoints';
import { useMe } from '../app/auth';
import { Icon, type IconName } from '../components/Icon';
import { PeriodPicker } from '../components/PeriodPicker';
import { ErrorBox, Loading, Money, ReceiptBadge } from '../components/ui';
import { formatDate, formatDateTime, yearMonthLabel } from '../lib/dates';

/** Seçili ay: net toplam, brüt, iade/iptal etkisi, aktif fiş, kendi net harcaması, son kayıtlar (PDF §04). */
export function DashboardScreen() {
  const me = useMe();
  const [periodId, setPeriodId] = useState<string | undefined>();
  const q = useQuery({ queryKey: ['dashboard', periodId], queryFn: () => ep.dashboard(periodId) });
  const d = q.data;
  const credit = useQuery({ queryKey: ['credit', 'open'], queryFn: () => ep.creditPayments('open') });
  const soon = (credit.data ?? []).filter((r) => r.days_left !== null && r.days_left <= 7);

  return (
    <div className="stack">
      <h1>Merhaba, {me.user.full_name.split(' ')[0]}</h1>
      <ErrorBox error={q.error} />
      {soon.length > 0 && (
        <Link to="/odemeler" className={`alert ${soon.some((r) => (r.days_left ?? 1) <= 0) ? 'bad' : 'warn'} credit-banner`}>
          <Icon name="calendar" size={20} />
          <span style={{ flex: 1 }}>
            <strong>{soon.length} veresiye ödemesi</strong> 7 gün içinde (ya da gecikmiş):{' '}
            {soon.slice(0, 3).map((r) => `${r.vendor_name ?? r.category} · ${formatDate(r.payment_due_date)}`).join(', ')}
            {soon.length > 3 ? '…' : ''}
          </span>
          <Icon name="chevron" size={18} />
        </Link>
      )}
      {!d ? <Loading /> : (
        <>
          <section className="hero" aria-label="Ay özeti">
            <div className="hero-top">
              <span className="hero-label">{yearMonthLabel(d.period.year_month)} · Net toplam gider</span>
              <div style={{ minWidth: 0 }}><PeriodPicker value={periodId ?? d.period.id} onChange={setPeriodId} /></div>
            </div>
            <div className="hero-value"><Money value={d.totals.net_kurus} /></div>
            <div className="hero-meta">
              <span className="pill">{d.period.status === 'CLOSED' ? 'Kapalı ay' : 'Açık ay'}</span>
              <span className="pill">{d.counts.active} aktif fiş</span>
              <span>Güncelleme: {formatDateTime(d.as_of)}</span>
            </div>
          </section>

          <div className="grid">
            <Tile icon="receipt" label="Brüt gider" value={<Money value={d.totals.gross_kurus} />} />
            <Tile icon="undo" label="İade etkisi" value={<Money value={d.totals.refund_kurus} />} />
            <Tile icon="close" label="İptal etkisi" value={<Money value={d.totals.cancel_kurus} />} />
            <Tile icon="users" label="Benim net harcamam" value={<Money value={d.my_net} />} />
          </div>

          <p className="small muted" style={{ margin: '0 4px' }}>
            Her fiş, <strong>fişin üzerindeki tarihin ayına</strong> yazılır. Önceki aya ait bir fiş eklerseniz yukarıdaki menüden o ayı seçin.
          </p>
          <div className="card">
            <div className="row"><h2 style={{ margin: 0 }}>Son kayıtlar</h2><span className="spacer" /><Link to="/havuz" className="small">Tümü</Link></div>
            <ul className="list" style={{ marginTop: 6 }}>
              {d.recent.map((r) => (
                <li key={r.id}>
                  <Link to={`/fis/${r.id}`}>
                    <span className="tile-ico" style={{ margin: 0 }}><Icon name="receipt" size={18} /></span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="title">{r.category}</div>
                      <div className="small muted">{r.owner_name} · {formatDate(r.document_date)}</div>
                      {r.document_date.slice(0, 7) !== d.period.year_month && (
                        <span className="badge info" title="Fiş, belge tarihinin ayına yazılır">{yearMonthLabel(r.document_date.slice(0, 7))} dönemine yazıldı</span>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 700 }}><Money value={r.amount} /></div>
                      <ReceiptBadge status={r.status} />
                    </div>
                  </Link>
                </li>
              ))}
              {!d.recent.length && <li className="empty">Henüz kayıt yok. Sağ alttaki “Fiş ekle” ile ilk fişi ekleyin.</li>}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

function Tile({ icon, label, value }: { icon: IconName; label: string; value: ReactNode }) {
  return (
    <div className="card stat">
      <span className="tile-ico"><Icon name={icon} size={18} /></span>
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
}
